import React, { useEffect, useState } from 'react';
import FeaturesCarousel from '../featuresCarousel/FeaturesCarousel';
import styles from './FeaturesSection.module.scss';
import { FeaturesAndCapabilitiesResponsePayloadService } from '../../@types/dataTypes';
import { CONSTANTS } from '../../constants/constants';
import FeaturesAccordion from '../featuresAccordion/FeaturesAccordion';
import { Text } from '@tme/toyota-common';
import { VinCheckerCSFeaturesMainLabels } from '../../@types/labelTypes';
import {
	mapLabelsWithVehicleData,
	replacePlaceHoldersInLabel,
	updateOrderOfServices
} from '../../tools/tools';
import { useSetRecoilState } from 'recoil';
import { featuresAndCapabilitiesAtom } from '../../store/atoms/vehicleData';

const { BREAKPOINTS } = CONSTANTS;

type FeaturesSectionProps = {
	features: FeaturesAndCapabilitiesResponsePayloadService[];
	labels: VinCheckerCSFeaturesMainLabels;
};

export default function FeaturesSection({
	features,
	labels
}: FeaturesSectionProps): React.JSX.Element {
	const [isMobile, setIsMobile] = useState<boolean>();
	const setFeaturesData = useSetRecoilState<
		FeaturesAndCapabilitiesResponsePayloadService[]
	>(featuresAndCapabilitiesAtom);

	useEffect(() => {
		const orderedServices = updateOrderOfServices(features);
		const mappedServices = mapLabelsWithVehicleData(
			orderedServices,
			labels
		);

		if (JSON.stringify(features) !== JSON.stringify(mappedServices)) {
			setFeaturesData(mappedServices);
		}
	}, [features, labels, setFeaturesData]);

	useEffect(() => {
		const handleResize = (): void => {
			setIsMobile(window.innerWidth <= BREAKPOINTS.MEDIUM);
		};
		handleResize();

		window.addEventListener(`resize`, handleResize);
		return () => {
			window.removeEventListener(`resize`, handleResize);
		};
	}, []);

	return (
		<div className={`${styles.featuresSectionWrapper}`}>
			<div className={`${styles.titleDescriptionWrapper}`}>
				<Text kind={`HEADER_2`} testId={`feature-section-title`}>
					{labels.vinCheckerCSFeaturesLabels.title}
				</Text>
				<Text
					kind={`PARAGRAPH_STRONG`}
					testId={`feature-section-description`}
				>
					{replacePlaceHoldersInLabel(
						labels.vinCheckerCSFeaturesLabels.description
					)}
				</Text>
			</div>
			{isMobile ? (
				<FeaturesAccordion
					features={features || []}
					labels={labels.vinCheckerCSFeaturesLabels}
				/>
			) : (
				<FeaturesCarousel
					features={features || []}
					labels={labels.vinCheckerCSFeaturesLabels}
				/>
			)}
			<div className={`${styles.divider} cp-divider`} />
			<div className={`${styles.noteWrapper}`}>
				<Text
					kind={`CAPTION`}
					className={`${styles.note}`}
					testId={`feature-section-note`}
				>
					{`*${labels.vinCheckerCSFeaturesLabels.note}`}
					<a
						href={labels.vinCheckerCSFeaturesLabels.oneAppLink}
						target={`_blank`}
						rel={`noopener noreferrer`}
						className={`dxp-icon icon-External_Link-small ${styles.appLinkIcon}`}
						aria-label={`Go to external page`}
					/>
				</Text>
			</div>
			<Text
				kind={`CAPTION`}
				className={`${styles.note}`}
				testId={`services-disclaimer-note`}
			>
				{`*${replacePlaceHoldersInLabel(
					labels.vinCheckerCSFeaturesLabels.servicesDisclaimer
				)}`}
			</Text>
		</div>
	);
}
